// Where a chat lands when it opens, and whether the row it lands on flashes the open-at-message
// highlight. Two rules, one answer, because they are the same decision seen from two sides.
//
// The ordinary rule: `?message=` (a Chats search hit, a notification card, a shared link) scrolls to that
// row and highlights it — "you were brought here".
//
// ⚠️ The BRIEF rule: a morning brief has NO target and NO highlight. It always lands at the top of the
// round-table's "Morning meeting summary" card — the wrap-up is what the reader came for, and it is long
// enough that the tail hides its start. This matters because the brief's own link carries `?message=`
// (and both phones' brief push deep-links the same `…/chat/<briefId>/message/<openingId>`), so a brief
// opened from one used to scroll to — and flash the highlight on — whichever turn the notification
// happened to name. Dropping the target rather than merely suppressing the highlight also stands the
// target page-hunt down: there is nothing to page back to.
//
// ⚠️ And a conversation we have not CLASSIFIED yet has no target either, which is the half that is easy
// to miss. Web learns brief-ness from the conversation ROW, which is fetched in parallel with the
// transcript — so for the frames before it lands (and forever, if that fetch fails) an older or unlisted
// brief reached by link looks exactly like a plain chat, and the target would scroll, highlight and start
// paging before anyone could know better. Withholding the target until classification settles costs
// nothing, because the target row cannot be scrolled to before the transcript it lives in has loaded
// anyway. The phones never face this: they resolve the kind BEFORE choosing a reader
// (`openAfterConversationKindLookup` / `launchConversationKindLookup`), so the open already knows.
//
// Shared 1:1 with iOS `ChatStore.beginConversationOpen` and Android
// `SymphonyStore.prepareOpenConversation` / `openChatTargetRouted`, which drop the target and switch to
// the brief landing from the same `source === 'BRIEF'`.

export interface OpenScrollInputs {
  /** The open conversation is known to be a morning brief. */
  isBrief: boolean;
  /**
   * Whether we yet know WHICH it is. False only while the answer is genuinely still open — the
   * conversation row has not resolved AND its transcript has not settled. Never leave this permanently
   * false on a failure path: a plain chat deep-linked at a message would then never scroll to it.
   */
  classificationSettled: boolean;
  /** `?message=` — the row an open-at-message link named, if any. */
  targetMessageId?: string | null;
  /**
   * The newest `agentMeetingSummary` row in the loaded transcript, when there is one. A brief carrying
   * none scrolls nowhere, where native falls back to its last row: native's brief is top-anchored and
   * needs a row to aim at, web's transcript already rests at the bottom, so the two settle in the same
   * place.
   */
  summaryMessageId?: string | null;
}

export interface OpenScroll {
  /** The target the open-at-message machinery hunts for — null for a brief, so it never runs. */
  openTarget: string | null;
  /** The row to scroll to on open. */
  scrollToMessageId: string | null;
  /** Whether that row flashes the "you were brought here" highlight. Never for a brief. */
  highlightOnOpen: boolean;
}

export function resolveOpenScroll({
  isBrief,
  classificationSettled,
  targetMessageId,
  summaryMessageId,
}: OpenScrollInputs): OpenScroll {
  const openTarget = isBrief || !classificationSettled ? null : targetMessageId ?? null;
  return {
    openTarget,
    scrollToMessageId: openTarget ?? (isBrief ? summaryMessageId ?? null : null),
    highlightOnOpen: openTarget !== null,
  };
}
